import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getToken } from '../../../../features/token/tokenSaver';
import posthog from '../../../../services/posthog';
import { RingLoader } from 'react-spinners';
import { patchAdvertiserOnboarding } from '../../../../services/patchAdvertiserOnboarding';

const STORAGE_META = 'invert_new_campaign_meta';
const STORAGE_H_TOKEN = 'invert_new_campaign_horizontal_token';
const STORAGE_V_TOKEN = 'invert_new_campaign_vertical_token';

export default function AdvertiserNewCampaignStripeSuccess() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = useSelector(getToken);
    const sessionId = searchParams.get('session_id');

    const [phase, setPhase] = useState('working');
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            if (!sessionId) {
                setPhase('error');
                setErrorMessage('Missing payment session. Start again from New Campaign.');
                return;
            }

            if (!token) {
                return;
            }

            let meta = {};
            try {
                const raw = sessionStorage.getItem(STORAGE_META);
                if (raw) meta = JSON.parse(raw);
            } catch {
                meta = {};
            }

            if (!meta.campaignName) {
                setPhase('error');
                setErrorMessage('Missing campaign details. Return to New Campaign and try checkout again.');
                return;
            }

            if (!Array.isArray(meta.selectedScreenIds) || meta.selectedScreenIds.length === 0) {
                setPhase('error');
                setErrorMessage('Missing screen selection. Return to New Campaign and try checkout again.');
                return;
            }

            const horizontalToken = sessionStorage.getItem(STORAGE_H_TOKEN);
            const verticalToken = sessionStorage.getItem(STORAGE_V_TOKEN);

            if (!horizontalToken || !verticalToken) {
                setPhase('error');
                setErrorMessage(
                    'We could not restore your creative files after payment. Return to New Campaign and try checkout again from the same browser.'
                );
                return;
            }

            if (!token) {
                setPhase('error');
                setErrorMessage('You are not signed in. Log in as an advertiser, then open this success link again from your email or history.');
                return;
            }

            try {
                const res = await fetch(
                    `${process.env.REACT_APP_API_URL}/advertiser/campaign-stripe/finalize`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: token,
                        },
                        body: JSON.stringify({
                            sessionId,
                            campaignName: meta.campaignName,
                            horizontalCreativeToken: horizontalToken,
                            verticalCreativeToken: verticalToken,
                            categoryId: meta.categoryId,
                            storefront: meta.storefront,
                            screenIds: meta.selectedScreenIds,
                        }),
                    }
                );

                const data = await res.json().catch(() => ({}));
                if (!res.ok) {
                    throw new Error(data.error || 'Could not complete your campaign submission.');
                }

                if (!cancelled) {
                    sessionStorage.removeItem(STORAGE_H_TOKEN);
                    sessionStorage.removeItem(STORAGE_V_TOKEN);
                    sessionStorage.removeItem(STORAGE_META);
                    sessionStorage.removeItem('invert_new_campaign_horizontal');
                    sessionStorage.removeItem('invert_new_campaign_vertical');
                    posthog.capture('campaign created', {
                        campaign_name: meta.campaignName,
                        monthly_fee: meta.monthlyFee,
                        subscription_id: data.subscriptionID,
                        payment: 'stripe_one_time',
                    });
                    try {
                        await patchAdvertiserOnboarding(token, { stepId: 'first_campaign_submitted' });
                        await patchAdvertiserOnboarding(token, { complete: true });
                    } catch (onboardingErr) {
                        console.error('onboarding complete after campaign:', onboardingErr);
                    }
                    setPhase('done');
                    navigate('/advertiserDashboard/campaigns', { replace: true });
                }
            } catch (e) {
                if (!cancelled) {
                    posthog.captureException(e, { step: 'stripe campaign finalize', campaign_name: meta.campaignName });
                    setPhase('error');
                    setErrorMessage(e.message || 'Something went wrong.');
                }
            }
        };

        run();
        return () => {
            cancelled = true;
        };
    }, [sessionId, token, navigate]);

    if (phase === 'error') {
        return (
            <div className="advertiserDashboardContainerCampaigns">
                <div className="adminDashboardScreenPageContainer">
                    <div className="campaignModalAlert" style={{ maxWidth: '640px' }}>
                        <span className="registeredScreensTitle advertiserListTitle">Payment received</span>
                        <p style={{ padding: '1rem', textAlign: 'center', color: '#C52F00' }}>{errorMessage}</p>
                        <Link className="advertiserRegisterSubmitBtn" style={{ display: 'inline-block', textDecoration: 'none', textAlign: 'center' }} to="/advertiserDashboard/newCampaign">
                            Back to New Campaign
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="advertiserDashboardContainerCampaigns">
            <div className="adminDashboardScreenPageContainer">
                <div className="campaignModalAlert" style={{ maxWidth: '640px' }}>
                    <span className="registeredScreensTitle advertiserListTitle">Confirming payment…</span>
                    <p style={{ padding: '1rem', textAlign: 'center' }}>Creating your campaign submission. Please wait.</p>
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '1rem' }}>
                        <RingLoader color="#C52F00" loading size={50} aria-label="Loading" />
                    </div>
                </div>
            </div>
        </div>
    );
}
